import { apiClient, handleApiResponse, handleApiError } from "../config";
import { 
  AuthCredentials, 
  AuthResponse, 
  UserRegistration, 
  ShopkeeperRegistration, 
  UserProfile, 
  ApiResponse 
} from "../types";
import { TokenManager } from "../config";

export class AuthAPI {
  /**
   * Register a new user
   */
  static async registerUser(data: UserRegistration): Promise<AuthResponse> {
    try {
      const response = await apiClient.post('/api/v1/auth/register', data);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Request OTP
   */
  static async requestOtp(recipient: string, purpose: 'login' | 'register' | 'reset_pin' = 'login'): Promise<ApiResponse<string>> {
    try {
      const response = await apiClient.post('/api/v1/auth/otp/request', { recipient, purpose });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Verify OTP
   */
  static async verifyOtp(data: { recipient: string; otp: string; purpose?: 'login' | 'register' | 'reset_pin' }): Promise<AuthResponse> {
    try {
      const response = await apiClient.post('/api/v1/auth/otp/verify', {
        ...data,
        purpose: data.purpose || 'login'
      });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Login with PIN
   */
  static async loginWithPin(identifier: string, pin: string): Promise<AuthResponse> {
    try {
      const response = await apiClient.post('/api/v1/auth/login/pin', { identifier, pin });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Signup with PIN
   */
  static async signupWithPin(data: { 
    recipient: string; 
    otp: string; 
    pin: string; 
    full_name: string; 
    shop_name: string;
    is_firebase_verified?: boolean;
  }): Promise<AuthResponse> {
    try {
      const response = await apiClient.post('/api/v1/auth/signup/pin', data);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Reset PIN
   */
  static async resetPin(data: { 
    recipient: string; 
    otp: string; 
    new_pin: string;
    is_firebase_verified?: boolean;
  }): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.post('/api/v1/auth/reset-pin', data);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * User login
   */
  static async login(credentials: AuthCredentials): Promise<AuthResponse> {
    try {
      const response = await apiClient.post('/api/v1/auth/login', credentials);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * User registration
   */
  static async register(userData: UserRegistration): Promise<AuthResponse> {
    try {
      const response = await apiClient.post('/api/v1/auth/register', userData);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Shopkeeper registration
   */
  static async registerShopkeeper(shopData: ShopkeeperRegistration): Promise<AuthResponse> {
    try {
      // Use the same register endpoint but with is_shopkeeper: true
      const payload = {
        ...shopData,
        is_shopkeeper: true
      };
      const response = await apiClient.post('/api/v1/auth/register', payload);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Update User Profile
   */
  static async updateProfile(data: { full_name?: string; shop_name?: string; pin?: string; shop_address?: string }): Promise<UserProfile> {
    try {
      const response = await apiClient.post('/api/v1/auth/profile/update', data);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Get current user profile
   */
  static async getCurrentUser(): Promise<UserProfile> {
    try {
      const response = await apiClient.get('/api/v1/auth/me');
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Refresh access token
   */
  static async refreshToken(): Promise<AuthResponse> {
    try {
      const response = await apiClient.post('/api/v1/auth/refresh');
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Logout user
   */
  static async logout(): Promise<void> {
    try {
      await apiClient.post('/api/v1/auth/logout');
    } catch (error) {
      // Don't throw error on logout - just log it
      console.warn('Logout error:', error);
    }
  }

  /**
   * Verify email
   */
  static async verifyEmail(token: string): Promise<ApiResponse<string>> {
    try {
      const response = await apiClient.post(`/api/v1/auth/verify-email/${token}`);
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Request password reset
   */
  static async requestPasswordReset(email: string): Promise<ApiResponse<string>> {
    try {
      const response = await apiClient.post('/api/v1/auth/password-reset-request', { email });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Reset password
   */
  static async resetPassword(token: string, newPassword: string): Promise<ApiResponse<string>> {
    try {
      const response = await apiClient.post(`/api/v1/auth/password-reset/${token}`, {
        new_password: newPassword,
      });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Change password
   */
  static async changePassword(currentPassword: string, newPassword: string): Promise<ApiResponse<string>> {
    try {
      const response = await apiClient.post('/api/v1/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }
}

// Export default instance for convenience
export const authAPI = AuthAPI;